import React, { useState } from "react";
import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import {
  Menu, Search, Bell, Newspaper, Calendar, Plus, Briefcase, Grid, Building2,
  Trophy, Layers, Store, MessageSquare, Bookmark, Settings, ShieldCheck, X, LogIn, LogOut,
} from "lucide-react";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";
import { PublishUpdateModal } from "@/components/PublishUpdateModal";
import { cn } from "@/lib/utils";

const drawerItems = [
  { label: "Updates", icon: Newspaper, to: "/" },
  { label: "Govt & Health", icon: Building2, to: "/govt-health" },
  { label: "Communities", icon: Layers, to: "/communities" },
  { label: "Upcoming Events", icon: Calendar, to: "/events" },
  { label: "Achievements", icon: Trophy, to: "/achievements" },
  { label: "Marketplace", icon: Store, to: "/opportunities" },
];
const youItems = [
  { label: "Messages", icon: MessageSquare, to: "/messages" },
  { label: "Saved", icon: Bookmark, to: "/saved" },
  { label: "Settings", icon: Settings, to: "/settings" },
];

function Logo({ light = false }) {
  return (
    <div className="flex items-center gap-2">
      <div className={cn("flex h-9 w-9 items-center justify-center rounded-xl", light ? "bg-white" : "bg-white border border-slate-200 shadow-sm")}>
        <svg viewBox="0 0 48 24" className="h-5 w-5" fill="none">
          <path d="M8 12a6 6 0 1 1 12 0 6 6 0 1 1-12 0Z" stroke="#F26A21" strokeWidth="3" />
          <path d="M28 12a6 6 0 1 1 12 0 6 6 0 1 1-12 0Z" stroke="#0E1C3A" strokeWidth="3" />
        </svg>
      </div>
      <div className="leading-none">
        <div className="font-heading text-lg font-extrabold tracking-tight">
          <span className="text-medorange">Med</span><span className={light ? "text-white" : "text-navy"}>Lync</span>
        </div>
        <div className={cn("text-[9px] font-bold uppercase tracking-[0.18em]", light ? "text-slate-300" : "text-slate-400")}>
          Ahmedabad Healthcare Network
        </div>
      </div>
    </div>
  );
}

export default function Layout() {
  const [drawer, setDrawer] = useState(false);
  const [publish, setPublish] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const bottomNav = [
    { id: "bottom-nav-updates", label: "Updates", icon: Newspaper, to: "/" },
    { id: "bottom-nav-events", label: "Events", icon: Calendar, to: "/events" },
    { id: "bottom-nav-post-cta", label: "Post", icon: Plus, cta: true },
    { id: "bottom-nav-opportunities", label: "Market", icon: Briefcase, to: "/opportunities" },
    { id: "bottom-nav-more", label: "More", icon: Grid, to: "/more" },
  ];

  const isActive = (to) => (to === "/" ? location.pathname === "/" : location.pathname.startsWith(to));
  const initials = user && user.name ? user.name.split(" ").map((w) => w[0]).slice(0, 2).join("") : "";

  const handlePost = () => {
    if (user && user.id) setPublish(true);
    else navigate("/login");
  };

  return (
    <div className="ml-shell pb-24">
      {/* Header */}
      <header data-testid="app-header" className="sticky top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-white/90 px-4 py-3 backdrop-blur-md">
        <div className="flex items-center gap-3">
          <button data-testid="hamburger-drawer-button" onClick={() => setDrawer(true)} className="text-navy transition-colors hover:text-medorange">
            <Menu className="h-6 w-6" />
          </button>
          <Logo />
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigate("/")} className="text-slate-500 hover:text-navy" aria-label="search"><Search className="h-5 w-5" /></button>
          <button className="relative text-slate-500 hover:text-navy" aria-label="notifications">
            <Bell className="h-5 w-5" />
            <span className="absolute -right-0.5 -top-0.5 h-2 w-2 rounded-full bg-medorange" />
          </button>
          {user && user.id ? (
            <button onClick={() => navigate("/more")} data-testid="header-avatar">
              <Avatar className="h-9 w-9 ring-2 ring-medorange/40">
                <AvatarFallback className="bg-medorange text-sm font-bold text-white">{initials}</AvatarFallback>
              </Avatar>
            </button>
          ) : (
            <button data-testid="header-login-button" onClick={() => navigate("/login")}
              className="flex items-center gap-1.5 rounded-full bg-navy px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-navy-hover">
              <LogIn className="h-3.5 w-3.5" /> Sign in
            </button>
          )}
        </div>
      </header>

      <main className="min-h-[60vh]">
        <Outlet context={{ openPublish: () => setPublish(true) }} />
      </main>

      {/* Drawer */}
      <Sheet open={drawer} onOpenChange={setDrawer}>
        <SheetContent side="left" data-testid="side-drawer-container" className="w-72 border-0 bg-navy p-0 text-white navy-grid">
          <div className="flex items-center justify-between px-5 py-5">
            <Logo light />
            <button onClick={() => setDrawer(false)} className="rounded-md border border-white/20 p-1 text-white"><X className="h-4 w-4" /></button>
          </div>
          <div className="px-3 py-2">
            <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">Menu</p>
            {drawerItems.map((it) => (
              <Link key={it.to} to={it.to} onClick={() => setDrawer(false)}
                className={cn("my-0.5 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors",
                  isActive(it.to) ? "bg-medorange font-semibold text-white" : "text-slate-300 hover:bg-white/10 hover:text-white")}>
                <it.icon className="h-5 w-5" /> {it.label}
              </Link>
            ))}
            <p className="px-3 pb-1 pt-4 text-[10px] font-bold uppercase tracking-widest text-slate-400">You</p>
            {youItems.map((it) => (
              <Link key={it.to} to={it.to} onClick={() => setDrawer(false)}
                className="my-0.5 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-300 transition-colors hover:bg-white/10 hover:text-white">
                <it.icon className="h-5 w-5" /> {it.label}
              </Link>
            ))}
            {user && user.role === "admin" && (
              <Link to="/admin" onClick={() => setDrawer(false)}
                className={cn("my-0.5 flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-colors",
                  isActive("/admin") ? "bg-medorange text-white" : "text-slate-300 hover:bg-white/10 hover:text-white")}>
                <ShieldCheck className="h-5 w-5" /> Admin
              </Link>
            )}
            <div className="mt-3 border-t border-white/10 pt-3">
              {user && user.id ? (
                <button onClick={() => { setDrawer(false); logout(); }} className="flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-300 hover:bg-white/10">
                  <LogOut className="h-5 w-5" /> Sign out
                </button>
              ) : (
                <Link to="/login" onClick={() => setDrawer(false)} className="flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium text-slate-300 hover:bg-white/10">
                  <LogIn className="h-5 w-5" /> Sign in
                </Link>
              )}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Bottom nav */}
      <nav data-testid="bottom-nav-container" className="fixed bottom-0 left-1/2 z-40 flex h-16 w-full max-w-[480px] -translate-x-1/2 items-center justify-around border-t border-slate-200 bg-white/95 px-2 backdrop-blur-md md:max-w-[720px]">
        {bottomNav.map((n) =>
          n.cta ? (
            <button key={n.id} data-testid={n.id} onClick={handlePost}
              className="-mt-6 flex h-14 w-14 flex-col items-center justify-center rounded-full border-4 border-white bg-medorange text-white shadow-lg shadow-orange-500/40 transition-transform active:scale-95">
              <Plus className="h-6 w-6" />
            </button>
          ) : (
            <button key={n.id} data-testid={n.id} onClick={() => navigate(n.to)}
              className={cn("flex flex-1 flex-col items-center gap-0.5 text-[11px] font-semibold transition-colors",
                isActive(n.to) ? "text-medorange" : "text-slate-400 hover:text-navy")}>
              <n.icon className="h-5 w-5" /> {n.label}
            </button>
          )
        )}
      </nav>

      <PublishUpdateModal open={publish} onOpenChange={setPublish} />
    </div>
  );
}
